const endpoint = process.env.REACT_APP_API_ENDPOINT ?? "";

const loginEndpoint = `${endpoint}/api/auth/samlLogin` ?? "/api/auth/samlLogin";
// Logout must target the same origin that owns the authentication cookie (matching the
// app's relative "/api" base). Using the absolute REACT_APP_API_ENDPOINT here sends logout
// to a different backend on bridged sub-domains, leaving the local session cookie intact.
const logoutEndpoint = "/api/auth/samlLogout";
const samlLoginEndpoint = `${endpoint}/api/auth/samlLogin` ?? "/api/auth/samlLogin";
const samlLogoutEndpoint = "/api/auth/samlLogout";
const proxyLogoutEndpoint = `${endpoint}/api/auth/endProxySession` ?? "/api/auth/endProxySession";

export const getCurrentUrl = () => {
  return window.location.href;
};

export const getCurrentDomain = () => {
  return window.location.protocol + '//' + window.location.host;
}

export const getCurrentUrlWithoutPath = () => {
  return window.location.href.split("?")[0].split("/").slice(0, -1).join("/")
};

export const getLoggedOutEndpoint = () => {
  return (
    getCurrentDomain() +
    "/home?action=logout"
  );
};

export const getApplyEndpoint = () => {
  return (
    window.location.href.split("?")[0].split("/").slice(0, -1).join("/") +
    "/apply"
  );
};

export const getCurrentEndpoint = () => {
  return (
    window.location.href.split("?")[0].split("/").slice(0, -1).join("/") +
    "/apply"
  );
};

// Origin of the generic/public portal (e.g. payments.alberta.ca), derived from the
// configured API endpoint. Sign-in must complete on this origin so the SAML assertion
// and session cookie land there before the user is bridged to a program sub-domain.
const getPublicPortalOrigin = () => {
  if (!endpoint) {
    return "";
  }

  try {
    return new URL(endpoint).origin;
  } catch {
    return "";
  }
};

// When sign-in is initiated from a program-specific sub-domain (a cross-domain portal
// such as evacuationpayment.alberta.ca), route the post-authentication callback through
// the public portal, carrying this sub-domain's origin as a `bridgeReturn` marker
// (payments.alberta.ca/?bridgeReturn=<sub-domain origin>). After authentication completes
// on the public portal, the bootstrap redirect returns the user to this sub-domain with a
// `bridgeSource` marker, and the session-bridge flow establishes the session here. When
// already on the public portal, the original callback is returned unchanged so the
// existing sign-in flow is unaffected.
export const resolveLoginCallbackUrl = (callBackUrl) => {
  const defaultCallback = callBackUrl || getCurrentUrl();
  const publicPortalOrigin = getPublicPortalOrigin();

  if (
    !publicPortalOrigin ||
    publicPortalOrigin.toLowerCase() === getCurrentDomain().toLowerCase()
  ) {
    // Encode so any `?`/`&`/`#` in the callback URL survives as a single `callback=`
    // query value instead of being split into extra query parameters on the server.
    return encodeURIComponent(defaultCallback);
  }

  const bridgeReturn = encodeURIComponent(getCurrentDomain());
  return encodeURIComponent(`${publicPortalOrigin}/?bridgeReturn=${bridgeReturn}`);
};

export const getLoginEndPoint = (callBackUrl) => {
  return `${loginEndpoint}?callback=${resolveLoginCallbackUrl(callBackUrl)}`;
};

export const getLogoutEndPoint = (callBackUrl) => {
  return `${logoutEndpoint}?callback=${callBackUrl}`;
};

export const getSamlLoginEndPoint = (callBackUrl) => {
  return `${samlLoginEndpoint}?callback=${resolveLoginCallbackUrl(callBackUrl)}`;
};

export const getSamlLogoutEndPoint = (callBackUrl) => {
  return `${samlLogoutEndpoint}?callback=${callBackUrl}`;
};

export const getProxyLogoutEndPoint = (applicationId) => {
  return applicationId 
    ? `${proxyLogoutEndpoint}?applicationId=${applicationId}`
    : `${proxyLogoutEndpoint}`;
};

export const endProxyAndRedirect = async (applicationId, programId) => {
    let internalPortalUrl = process.env.REACT_APP_INTERNAL_PORTAL_URL;
    let targetUrl;
    if (applicationId) {
        targetUrl = `${internalPortalUrl}/summary/${applicationId}`;
    } else if (programId) {
        targetUrl = `${internalPortalUrl}/workspace/${programId}`;
    } else {
        targetUrl = `${internalPortalUrl}/workspace`;
    }

    try {
        // Send the kill signal in the background
        await fetch(getProxyLogoutEndPoint(applicationId), {
            method: 'GET',
            headers: { 'Accept': 'application/json' },
            redirect: 'manual'
        });
    } catch (error) {
        console.error("Session termination network error, forcing redirect.", error);
    } finally {
        //React forces the redirect
        window.location.href = targetUrl;
    }
};